import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

/**
 * ErrorBoundary — capture les erreurs runtime des composants enfants pour
 * éviter qu'un crash dans une page (ex. `null.toFixed()`) ne fasse écran
 * blanc sur toute l'application.
 *
 * Doit être un class component (les hooks React ne supportent pas
 * getDerivedStateFromError ni componentDidCatch).
 *
 * Usage :
 *   <ErrorBoundary fallback={<MaPage5xx/>}>
 *     <DashboardBreakers />
 *   </ErrorBoundary>
 *
 * Si un crash arrive ici, on affiche un message lisible avec un bouton
 * "Réessayer" qui force le re-mount du sous-arbre. Les autres routes ne
 * sont pas impactées.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Optionnel : remonter à un service de monitoring (Sentry, etc.)
    // window.dispatchEvent(new CustomEvent('app-error', { detail: { error, info } }));
    if (process.env.NODE_ENV !== 'production') {
      console.error('[ErrorBoundary]', error, info?.componentStack);
    }
  }

  reset = () => {
    this.setState({ hasError: false, error: null, info: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return typeof this.props.fallback === 'function'
          ? this.props.fallback({ error: this.state.error, reset: this.reset })
          : this.props.fallback;
      }

      return (
        <div className="min-h-[60vh] flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-slate-900 border border-red-900/50 rounded-lg p-6 text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-red-950/50 p-3 rounded-full">
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </div>
            </div>
            <h2 className="text-white text-xl font-semibold mb-2">
              Cette page a rencontré un problème
            </h2>
            <p className="text-slate-400 text-sm mb-4">
              {this.props.routeName
                ? `La page "${this.props.routeName}" n'a pas pu s'afficher correctement.`
                : 'Le composant n\'a pas pu s\'afficher correctement.'}
              {' '}Les autres parties de l'application restent fonctionnelles.
            </p>
            {process.env.NODE_ENV !== 'production' && this.state.error && (
              <pre className="text-left bg-slate-950 border border-slate-800 rounded p-3 text-[10px] text-red-300 overflow-auto max-h-40 mb-4">
                {String(this.state.error?.stack || this.state.error)}
              </pre>
            )}
            <div className="flex justify-center gap-2">
              <Button
                onClick={this.reset}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Réessayer
              </Button>
              <Button
                onClick={() => (window.location.href = '/')}
                variant="outline"
                className="border-slate-700"
              >
                Retour à l'accueil
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
